import React, { useCallback } from 'react'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Counter from 'components/Luxkit/Counter'
import { useAppDispatch } from 'hooks/reduxHooks'
import { updateBnblNumberOfRooms, updateBnblPackageView } from 'actions/BnblModalActions'
import { getBnblPackageViews, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { getBnblPackageViewByNumberOfRooms } from 'components/OfferPage/BnblModal/utils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Group from 'components/utils/Group'
import { checkCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { isCustomerSupportMode } from 'selectors/featuresSelectors'
import { sum } from 'lib/array/arrayUtils'

interface MappedProps {
  numberOfRooms: number;
  packageView: OfferPackageOptionView;
  baseBnblPackageViews: Array<OfferPackageOptionView>;
  canRedeemLuxPlusBenefits: boolean;
  baseMemberPrice: number;
  isCustomerSupportMode: boolean;
}

interface Props {
  offerId: string;
  packageKey: string;
}

function BnblPackageRooms({ baseBnblPackageViews, numberOfRooms, packageView, offerId, canRedeemLuxPlusBenefits, baseMemberPrice, isCustomerSupportMode }: Props & MappedProps) {
  const dispatch = useAppDispatch()
  const shouldPreventMultipleRoomsBooking = numberOfRooms >= 2 && canRedeemLuxPlusBenefits && baseMemberPrice > 0 && !isCustomerSupportMode

  const maxNumberOfRooms = shouldPreventMultipleRoomsBooking ? 2 : 10

  const onChangeNumberOfRooms = useCallback((numberOfRooms: number) => {
    const baseSelectedPackageView = baseBnblPackageViews.find(pkg => pkg.package.uniqueKey === packageView.package.uniqueKey)
    const updatedPackageView = getBnblPackageViewByNumberOfRooms(baseSelectedPackageView, numberOfRooms)

    dispatch(updateBnblNumberOfRooms(numberOfRooms, offerId, packageView.package.uniqueKey))
    dispatch(updateBnblPackageView(offerId, updatedPackageView))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseBnblPackageViews, packageView])

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
      <BodyText variant="large" weight="bold">Rooms</BodyText>
      <Counter
        defaultValue={numberOfRooms}
        min={1}
        max={maxNumberOfRooms}
        onChange={onChangeNumberOfRooms}
        data-testid="bnbl-package-rooms-counter"
      />
    </Group>
  )
}

export default withOfferPageAndReduxState((offerPageState, appState: App.State, ownProps: Props): MappedProps => {
  const bnblModalPackages = appState.offer.bnblModal[ownProps.offerId]
  const modalPackage = bnblModalPackages.find(p => p.packageView.package.uniqueKey === ownProps.packageKey)
  const baseMemberPrice = sum(bnblModalPackages, p => p.packageView.baseMemberPrice)

  const { numberOfRooms, packageView } = modalPackage
  return {
    baseMemberPrice,
    numberOfRooms,
    packageView,
    baseBnblPackageViews: getBnblPackageViews(offerPageState),
    canRedeemLuxPlusBenefits: checkCanRedeemLuxPlusBenefits(appState),
    isCustomerSupportMode: isCustomerSupportMode(appState),
  }
})(BnblPackageRooms)
